import type { RowStanding } from '@/features/microsoft/picker-chrome.js';
import type { SharePointPlace } from '@/lib/api/sharepoint.js';

/** Where the browser is standing, which is what an item id has to be read against. */
export interface BrowsePosition {
  siteId: string | null;
  driveId: string | null;
  /** The open folders above the current level, outermost first. Empty at a library's root. */
  folderItemIds: string[];
}

/** A row the browser can offer, addressed the way a place addresses it. */
export interface BrowseRow {
  kind: 'site' | 'library' | 'folder' | 'file';
  id: string;
}

/**
 * What a row can be, beside the list.
 *
 * `covered` and `contains` both mean the server would refuse an Add, and they exist separately
 * because the way out differs: a covered row needs nothing done, while a containing one needs the
 * narrower places removed first. They are two ends of the one overlap rule identity enforces —
 * `sharePointPlacesOverlap` is symmetric, so a row that swallows a place is refused exactly as a
 * row a place swallows is.
 */
export type { RowStanding };

/** What a place is called on screen — the name its owner gave it, or the one SharePoint gave it. */
function placeName(place: SharePointPlace): string {
  return place.alias ?? place.nameSnapshot;
}

/**
 * How a browsed row stands against the places already on the list.
 *
 * It mirrors identity's `sharePointPlacesOverlap`, and it has to: an Add the server refuses is a
 * button that can only fail, and this feature shipped exactly that — every row inside an added site
 * offered Add and answered with a conflict. The rule is duplicated rather than shared because the
 * two live in different repos, so the test below is what keeps them honest.
 *
 * A file can be a place of its own, and it can sit inside a wider one. Inside one it is already
 * granted by that place and indexed by the place's own source, so it is covered rather than free —
 * offering Add there contradicted the line above the list saying the whole level was already on it.
 * A file place covers nothing but itself, so it swallows no row and no row sits inside it.
 */
export function placeStanding(
  places: readonly SharePointPlace[],
  row: BrowseRow,
  position: BrowsePosition,
): RowStanding {
  if (row.kind === 'file') {
    const itself = places.some(
      (place) =>
        place.level === 'file' &&
        place.siteId === position.siteId &&
        place.driveId === position.driveId &&
        place.rootItemId === row.id,
    );
    if (itself) return { kind: 'added' };
    const cover = levelCover(places, position);
    return cover ? { kind: 'covered', by: cover } : { kind: 'free' };
  }

  const site =
    row.kind === 'site' ? row.id : (position.siteId ?? '\u0000no-site');
  const drive =
    row.kind === 'library' ? row.id : (position.driveId ?? '\u0000no-drive');
  const here = places.filter((place) => place.siteId === site);

  const exact = here.find((place) => {
    if (row.kind === 'site') return place.level === 'site';
    if (row.kind === 'library') {
      return place.level === 'library' && place.driveId === drive;
    }
    return (
      place.level === 'folder' &&
      place.driveId === drive &&
      place.rootItemId === row.id
    );
  });
  if (exact) return { kind: 'added' };

  const ancestor = here.find((place) => {
    if (place.level === 'site') return row.kind !== 'site';
    if (place.driveId !== drive) return false;
    if (place.level === 'library') return row.kind === 'folder';
    return (
      row.kind === 'folder' &&
      place.rootItemId !== null &&
      position.folderItemIds.includes(place.rootItemId)
    );
  });
  if (ancestor) return { kind: 'covered', by: placeName(ancestor) };

  const swallowed = here.filter((place) => {
    if (row.kind === 'site') return true;
    if (place.level === 'site') return false;
    if (place.driveId !== drive) return false;
    if (row.kind === 'library') return place.level !== 'library';
    return (
      (place.level === 'folder' || place.level === 'file') &&
      place.rootAncestorItemIds.includes(row.id)
    );
  });
  if (swallowed.length > 0) {
    return {
      kind: 'contains',
      blocking: swallowed.map((place) => ({
        id: place.id,
        name: placeName(place),
      })),
    };
  }

  return { kind: 'free' };
}

/**
 * The place the browser is standing INSIDE, if any — so a level can say once what would otherwise
 * be repeated on every row. Null at the site list, which is inside nothing.
 */
export function levelCover(
  places: readonly SharePointPlace[],
  position: BrowsePosition,
): string | null {
  if (position.siteId === null) return null;
  const covering = places.find((place) => {
    if (place.siteId !== position.siteId) return false;
    if (place.level === 'site') return true;
    if (place.driveId !== position.driveId) return false;
    if (place.level === 'library') return true;
    return (
      place.level === 'folder' &&
      place.rootItemId !== null &&
      position.folderItemIds.includes(place.rootItemId)
    );
  });
  return covering ? placeName(covering) : null;
}
